<script setup lang="ts">
  import { BottomSheet, Button, ButtonAppearance, ButtonSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import { SERP_FILTERS_TEXTS } from '../config/texts';

  interface SerpFilterTooltipSheetProps {
    title: string;
    text: string;
  }

  const props = defineProps<SerpFilterTooltipSheetProps>();

  interface SerpFilterTooltipSheetEmits {
    (e: 'close'): void;
  }

  const emit = defineEmits<SerpFilterTooltipSheetEmits>();

  const SHEET_Z_INDEX = 10;
  const LINE_SEPARATOR = '\n';

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  const lines = computed<string[]>(() =>
    props.text
      .split(LINE_SEPARATOR)
      .map((line) => line.trim())
      .filter(Boolean),
  );

  const onClose = async (): Promise<void> => {
    await bottomSheetRef.value?.close();
  };
</script>

<template>
  <div
    class="serp-filter-tooltip-sheet"
    data-testid="serp-filter-tooltip-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-filter-tooltip-sheet__sheet"
      :z-index="SHEET_Z_INDEX"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <template #header>
        <Typo
          class="serp-filter-tooltip-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="serp-filter-tooltip-sheet-title"
        >
          {{ title }}
        </Typo>
      </template>

      <div
        class="serp-filter-tooltip-sheet__content"
        data-testid="serp-filter-tooltip-sheet-text"
        @touchmove.stop
      >
        <Typo
          v-if="lines.length === 1"
          class="serp-filter-tooltip-sheet__text"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
        >
          {{ lines[0] }}
        </Typo>

        <ul
          v-else-if="lines.length > 1"
          class="serp-filter-tooltip-sheet__list"
        >
          <li
            v-for="(line, index) in lines"
            :key="index"
            class="serp-filter-tooltip-sheet__item"
          >
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ line }}
            </Typo>
          </li>
        </ul>
      </div>

      <template #footer>
        <Button
          class="serp-filter-tooltip-sheet__close"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.L"
          data-testid="serp-filter-tooltip-sheet-close"
          @click="onClose"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ SERP_FILTERS_TEXTS.tooltipClose }}
          </Typo>
        </Button>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-filter-tooltip-sheet {
    display: contents;

    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__content {
      color: var(--color-text-primary);
    }

    &__text {
      color: var(--color-text-primary);
      margin: 0;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
      list-style: disc outside;
      margin: 0;
      padding: 0 0 0 var(--spacing-l);
    }

    &__item {
      color: var(--color-text-primary);
    }

    &__close {
      width: 100%;
    }
  }
</style>
